/**
 * @fileoverview ListGroupRow — a single expandable group row in the List tab.
 *
 * Represents one PlaceId group. Clicking the row expands/collapses it to show
 * the contained market venue mappings via {@link ListMappingsPanel}.
 *
 * The **Unified Name** cell is clickable and calls `onOpenDrawer` to open
 * {@link UnifiedNameDrawer} with a Google Maps preview — replacing the
 */

import * as React from 'react';
import {
    Collapse,
    IconButton,
    TableCell,
    TableRow,
    Typography,
    useTheme,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { MarketVenueMappingRecord } from '../utils';
import LocationCell from './LocationCell';
import ListMappingsPanel from './ListMappingsPanel';

// ---------------------------------------------------------------------------
// Column width constants
// ---------------------------------------------------------------------------

/**
 * Percentage widths for the parent group-row table columns.
 * Order: chevron, UnifiedName, Location.
 */
export const LIST_COL_WIDTHS = {
    chevron:     '4%',
    unifiedName: '34%',
    location:    '62%',
} as const;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A group of {@link MarketVenueMappingRecord}s sharing the same `placeId`.
 */
export interface PlaceIdGroup {
    /** Google Places unique identifier; empty string for records without a placeId. */
    placeId: string;
    /** All records that belong to this group. */
    records: MarketVenueMappingRecord[];
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/** Props for {@link ListGroupRow}. */
export interface ListGroupRowProps {
    /** The PlaceId group data to render. */
    group: PlaceIdGroup;
    /** Whether this row is currently expanded (controlled from parent). */
    expanded: boolean;
    /** Toggles the expanded state in the parent. */
    onToggle: () => void;
    /**
     * Called when the Unified Name cell is clicked to open the map-preview drawer.
     *
     * @param unifiedName      - Venue display name.
     * @param formattedAddress - Formatted address for the map embed.
     * @param googleMapsUri    - Google Maps URI.
     */
    onOpenDrawer: (unifiedName: string, formattedAddress: string, googleMapsUri: string) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * A single expandable row representing one {@link PlaceIdGroup} in the List tab.
 *
 * Shows: Unified Name (clickable → map drawer), Location (City, State + Address).
 * Expanding reveals {@link ListMappingsPanel} with all mappings sharing the placeId.
 *
 * @param props.group        - The PlaceId group data.
 * @param props.expanded     - Whether the group is currently expanded.
 * @param props.onToggle     - Toggles expansion in the parent.
 * @param props.onOpenDrawer - Opens the UnifiedNameDrawer in the parent.
 */
const ListGroupRow: React.FC<ListGroupRowProps> = ({ group, expanded, onToggle, onOpenDrawer }) => {
    const { records } = group;
    const sampleRecord = records[0];
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <>
            <TableRow
                hover
                onClick={onToggle}
                sx={{
                    cursor: 'pointer',
                    bgcolor: expanded
                        ? isDark ? 'action.selected' : 'action.hover'
                        : undefined,
                }}
                aria-expanded={expanded}
            >
                {/* Expand/collapse chevron */}
                <TableCell padding="none" sx={{ width: LIST_COL_WIDTHS.chevron, pl: 0.5 }}>
                    <IconButton size="small" aria-label={expanded ? 'collapse group' : 'expand group'}>
                        {expanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                    </IconButton>
                </TableCell>

                {/* Unified Name — clickable to open UnifiedNameDrawer */}
                <TableCell sx={{ width: LIST_COL_WIDTHS.unifiedName }}>
                    {sampleRecord?.googleUnifiedName ? (
                        <Typography
                            variant="body2"
                            noWrap
                            component="span"
                            onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                onOpenDrawer(
                                    sampleRecord.googleUnifiedName ?? '',
                                    sampleRecord.formattedAddress ?? '',
                                    sampleRecord.googleMapsUri ?? '',
                                );
                            }}
                            sx={{
                                color: 'primary.main',
                                cursor: 'pointer',
                                fontWeight: 500,
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            {sampleRecord.googleUnifiedName}
                        </Typography>
                    ) : (
                        <Typography variant="body2" color="text.disabled">—</Typography>
                    )}
                </TableCell>

                {/* Location: City, State + formatted address */}
                <TableCell sx={{ width: LIST_COL_WIDTHS.location }}>
                    <LocationCell
                        city={sampleRecord?.masterVenueCity ?? sampleRecord?.marketVenueCity}
                        state={sampleRecord?.state}
                        formattedAddress={sampleRecord?.formattedAddress}
                    />
                </TableCell>
            </TableRow>

            {/* Expand panel */}
            <TableRow
                sx={{
                    '& td': { borderBottom: expanded ? undefined : 0 },
                    '&:hover': { bgcolor: 'transparent' },
                }}
            >
                <TableCell
                    colSpan={3}
                    sx={{
                        p: 0,
                        bgcolor: isDark ? 'grey.900' : 'grey.50',
                    }}
                >
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <ListMappingsPanel records={records} />
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

export default ListGroupRow;
